import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { KidEmpty } from '../../../../design-system/kid';
import { LearnerNotice, getLearnerNotices, markLearnerNoticeRead } from '../../api/learner';
import { useLearnerSpace } from '../../learner/LearnerSpace';
import { formatDayAndTime, useLearnerTimeZone, zoneLabel } from '../../learner/learnerTime';

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; notices: LearnerNotice[] };

/**
 * MC-05 — "Mis avisos": the learner's notices, inside IQMaxx and nowhere else.
 *
 * Every notice is one the server wrote from its governed catalogue — a class
 * coming up, a class that moved or was cancelled, new words from the teacher —
 * so this screen only shows them. It never shows money, names or report
 * details because none is ever written. A notice that is stale keeps its text
 * and loses its button (MC05-R08), and a button that is there opens a screen
 * that checks access again on its own (R09). Marking one read changes nothing
 * else (R07).
 */
const LearnerNotices: React.FC = () => {
  const { studentId } = useLearnerSpace();
  const tz = useLearnerTimeZone(studentId);
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  const fetchNotices = useCallback(() => {
    if (!studentId) return;
    setLoad({ status: 'loading' });
    getLearnerNotices(studentId)
      .then((res) => setLoad({ status: 'ready', notices: res.notices }))
      .catch(() => setLoad({ status: 'error' }));
  }, [studentId]);

  useEffect(fetchNotices, [fetchNotices]);

  const markRead = (noticeId: string) => {
    markLearnerNoticeRead(noticeId, studentId ?? undefined)
      .then(() =>
        setLoad((l) =>
          l.status === 'ready'
            ? {
                status: 'ready',
                notices: l.notices.map((n) => (n.noticeId === noticeId ? { ...n, read: true } : n)),
              }
            : l,
        ),
      )
      .catch(() => undefined);
  };

  const withTimes =
    load.status === 'ready' && load.notices.some((n) => n.startsAt) ? zoneLabel(tz) : null;

  return (
    <>
      <h1 className="hi">Mis avisos</h1>
      <p className="hi-s">Lo nuevo de tus clases. En tu casa reciben sus propios avisos.</p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando tus avisos…
        </p>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>Ahora no podemos mostrar tus avisos</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchNotices}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && load.notices.length === 0 && (
        <KidEmpty
          icon="🔔"
          title="No tienes avisos"
          detail="Cuando haya algo nuevo, lo verás aquí."
        />
      )}

      {withTimes && <p className="lrn-zone">{withTimes}</p>}

      {load.status === 'ready' &&
        load.notices.map((n) => (
          <section key={n.noticeId} className="lrn-status" aria-label={n.title}>
            <h2>
              {n.title}
              {!n.read && <span className="lrn-policy"> · nuevo</span>}
            </h2>
            <p>{n.body}</p>
            {n.startsAt && <p className="lrn-zone">{formatDayAndTime(n.startsAt, tz)}</p>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {n.link && (
                <Link className="ag-b" to={n.link}>
                  Ver
                </Link>
              )}
              {!n.read && (
                <button className="ag-b soft" onClick={() => markRead(n.noticeId)}>
                  Ya lo leí
                </button>
              )}
            </div>
          </section>
        ))}
    </>
  );
};

export default LearnerNotices;
